import React, { useState } from 'react';
import { CustomFieldDefinition, FieldDataType } from '@alucio/aws-beacon-amplify/src/models';
import { Collapsible, DNABox, DNAButton, DNAContextMenu, DNADivider, DNAText, Iffy } from '@alucio/lux-ui';
import { Controller, FieldError, FieldValues, useFieldArray } from 'react-hook-form';
import { CustomObjectFieldValue } from 'src/types/orms';
import { ComposableField } from './ComposableField';
import { ObjectFieldHeader } from './ObjectFieldHeader';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { ComposableVariant, RHForm, useComposableForm } from './ComposableForm';
import { internalObjectFields } from './InternalObjectFields';
import { v4 as uuid } from 'uuid';
import { useFieldPositions } from './useCustomFields';
import cloneDeep from 'lodash/cloneDeep';
import { multiTypeSort } from 'src/state/redux/selector/common';
import { getFieldGroups } from './ColumnCustomFieldsWrapper';
import useObjectRestrictionChecker from
  'src/hooks/useCRMMeetingFormHandler/objectRecordRestrictionCheck/useObjectRestrictionChecker';

export type ObjectFieldWrapperProps = {
    field: CustomFieldDefinition,
    childFields: CustomFieldDefinition[],
    isReadOnly?: boolean,
    variant?: ComposableVariant;
    values?: CustomObjectFieldValue[],
    disabled?: boolean,
    control: RHForm['control'],
  }

type ObjectFieldDetailProps = {
  field: CustomFieldDefinition,
  childFields: CustomFieldDefinition[],
  variant?: ComposableVariant;
  disabled?: boolean,
  control: RHForm['control'],
  prefixChildId: string,
  value: { [key: string]: string | string[] },
  errors?: Record<string, FieldError>,
}

const formatChildFieldIds = (
  childField: CustomFieldDefinition,
  prefixChildId: string,
  relativeChildFields: CustomFieldDefinition[]): CustomFieldDefinition => {
  // IF THE DEPENDENT FIELD ID IS NOT PART OF THE OBJECT, IT'LL USE THE ROOT (SAME ID)
  const isDependentFieldPresentInObject = relativeChildFields?.some(({ id }) => id === childField.dependentFieldId);
  const dependentFieldId = isDependentFieldPresentInObject
    ? `${prefixChildId}.${childField.dependentFieldId}` : childField.dependentFieldId;

  return {
    ...childField,
    id: `${prefixChildId}.${childField.id}`,
    dependentFieldId,
  }
}

export const ObjectFieldWrapper: React.FC<ObjectFieldWrapperProps> = (props) => {
  const { disabled, childFields, field, control, variant, ...rest } = props
  const { rhForm } = useComposableForm()
  const { append, fields: objects } = useFieldArray<FieldValues>(
    {
      control,
      name : field.id,
    },
  );
  const [isCollapsed, setIsCollapsed] = useState(!rest.isReadOnly)
  if (!childFields || childFields.length === 0) return null

  const addField = () => {
    const defaultValues = childFields.reduce((acc, childField) => {
      const defaultOption = childField.fieldValueDefinitions.find((option) => option.isDefault)
      if (childField.fieldType === FieldDataType.MULTICATEGORICAL) {
        acc[childField.id] = defaultOption ? [defaultOption.id] : [];
      }
      else {
        acc[childField.id] = defaultOption ? defaultOption.id : '';
      }
      return acc
    }, { })
    append({ ...defaultValues, id: uuid(), externalId: '' })
  }

  const removeObject = (index: number) => {
    const fieldValue = cloneDeep(rhForm.getValues()?.[field.id])

    if (!fieldValue) return
    if (!Array.isArray(fieldValue)) return

    // remove the item based on the index
    fieldValue.splice(index, 1)

    // reset form state
    rhForm.reset({
      ...rhForm.getValues(),
      [field.id]: fieldValue,
    }, {
      keepDirty: true,
    })
  }

  return (
    <DNABox fill appearance="col">
      <ObjectFieldHeader
        title={field.fieldLabel || field.fieldName}
        isCollapsed={isCollapsed}
        variant={variant}
        setIsCollapsed={setIsCollapsed}
        isRequired={field.required}
      />
      <Collapsible
        collapsed={isCollapsed}
        renderChildrenCollapsed
        style={{
          backgroundColor: variant === ComposableVariant.VIRTUAL ? colors['color-ui2-neutral-700'] : undefined,
        }}
      >
        <DNABox
          key={field.id}
          spacing={!objects.length ? undefined : 'md'}
          style={{
            borderColor: variant === ComposableVariant.DEFAULT ? colors['color-gray-80']
              : colors['color-ui2-neutral-600'],

            borderWidth: 0,
            paddingHorizontal: 18,
            paddingVertical: 16,
            backgroundColor: variant === ComposableVariant.VIRTUAL ? colors['color-white-transparent-6'] : undefined,
          }}
          appearance="col"
        >
          {!objects.length && (
            <Iffy is={rest.isReadOnly && !objects.length}>
              <DNABox appearance="col" spacing="sm">
                <DNAText style={{ color: colors['color-gray-300'] }} appearance="subtle">No record was added</DNAText>
              </DNABox>
            </Iffy>)}
          {objects.map((val, i) =>
            (<ObjectRow
              key={i}
              childFields={childFields}
              field={field}
              control={control}
              isReadOnly={rest.isReadOnly}
              value={val}
              prefixChildId={`${field.id}.${i}`}
              removeObject={removeObject}
              index={i}
              variant={variant}
              {...rest}
            />),
          )}
          <Iffy is={!rest.isReadOnly}>
            <DNAButton
              appearance={ variant === ComposableVariant.DEFAULT ? 'outline' : 'filled' }
              status="tertiary"
              iconLeft="plus"
              onPress={addField}
            >
              Add {field.fieldLabel || field.fieldName}
            </DNAButton>
          </Iffy>
        </DNABox>
      </Collapsible>
    </DNABox>

  )
}

interface ObjectRowProps extends ObjectFieldDetailProps {
  index: number,
  isReadOnly?: boolean,
  removeObject: (i: number) => void,
}

export interface ObjectRestriction {
  update: boolean,
  delete: boolean,
}

const getObjectsCustomFieldRestrictions = (
  object: ObjectFieldDetailProps['value'],
  field: CustomFieldDefinition,
  isReadOnly?: boolean): ObjectRestriction => {
  const restriction: ObjectRestriction = {
    update: !!isReadOnly,
    delete: !!isReadOnly,
  };

  if (field.objectSetting?.restriction) {
    restriction.update = !!isReadOnly ||
      !!((field.objectSetting.restriction.syncUpdate && object.externalId) ||
      (field.objectSetting.restriction.update && object.id));
    restriction.delete = !!isReadOnly ||
      !!((field.objectSetting.restriction.syncDelete && object.externalId) ||
      (field.objectSetting.restriction.delete && object.id));
  }

  return restriction;
};

const ObjectRow: React.FC<ObjectRowProps> = (props) => {
  const {
    index,
    field,
    removeObject,
    disabled,
    isReadOnly,
    value,
    ...rest
  } = props;

  // FIRST LEVEL OF RESTRICTIONS (BASED OFF THE CUSTOM FIELD)
  const objectCustomFieldRestriction = getObjectsCustomFieldRestrictions(value, field, isReadOnly);

  // SECOND LEVEL OF RESTRICTION (BASED OFF THE SPECIFIC OBJECT'S CONDITION)
  const objectSpecificRestriction = useObjectRestrictionChecker({ field, value });

  const restriction = {
    delete: objectCustomFieldRestriction.delete || objectSpecificRestriction.delete,
    update: objectCustomFieldRestriction.update || objectSpecificRestriction.update,
  };

  return (
    <DNABox appearance="col" spacing="sm" key={value.id.toString()}>
      <Iffy is={!restriction.delete}>
        <DNABox alignY="center" spacing="md" alignX="end" childFill={1}>
          <DNABox appearance="row">
            <DNAContextMenu>
              <DNAContextMenu.Anchor>
                <DNAButton
                  appearance="outline"
                  status={rest.variant === ComposableVariant.VIRTUAL ? 'subtle' : 'tertiary'}
                  iconLeft="dots-three-vertical-bold"
                />
              </DNAContextMenu.Anchor>
              <DNAContextMenu.Items>
                <DNAContextMenu.Item
                  collapseOnPress
                  delay={100}
                  icon="trash"
                  title="Delete"
                  onPress={() => removeObject(index)}
                />
              </DNAContextMenu.Items>
            </DNAContextMenu>
          </DNABox>
        </DNABox>
      </Iffy>
      <DNABox appearance="col">
        <ObjectFieldDetail
          field={field}
          disabled={restriction.update}
          value={value}
          {...rest}
        />
      </DNABox>
      <DNADivider height={3} style={{ marginTop: 10 }}/>
    </DNABox>
  );
}

const ObjectFieldDetail: React.FC<ObjectFieldDetailProps> = (props) => {
  const { disabled, childFields, prefixChildId, variant, value, control } = props
  const fieldPostions = useFieldPositions(childFields, true)
  const isTwoColumnsForm = fieldPostions.length > 1

  const sortCustomFields = childFields
    .sort((a, b) => multiTypeSort(a.order, b.order, { dir: 'asc' }))

  const groups = getFieldGroups(sortCustomFields);

  return (
    <DNABox>
      <HiddenFields value={value} control={control} prefix={prefixChildId}/>
      <DNABox appearance="col" fill>
        <Iffy is={!isTwoColumnsForm}>
          <DNABox fill appearance="col" spacing="md" keepSpacingLastItem>
            {
          childFields
            .map((field, i) => (
              <ComposableField
                key={`${value.id}-left-${i}`}
                field={formatChildFieldIds(field, prefixChildId, childFields)}
                defaultChildValue={value[field.id]}
                disabled={disabled}
                variant={variant}
              />
            ))
        }
          </DNABox>
        </Iffy>
        <Iffy is={isTwoColumnsForm}>
          {groups.map((groupCustomFields, idx) => (
            <DNABox appearance="col" spacing="sm" key={idx}>
              <Iffy is={groupCustomFields.groupType === 'COLUMN'}>
                <DNABox>
                  {groupCustomFields.fields.map((field, i) => (
                    <DNABox
                      key={field.id}
                      appearance="col"
                      fill
                      style={{
                        marginRight: i === 0 && groupCustomFields.fields.length > 1 ? 16 : 0,
                        marginLeft: i > 0 ? 16 : 0,
                        marginVertical: 8,
                      }}
                    >
                      <ComposableField
                        key={`${value.id}-right-${i}`}
                        field={formatChildFieldIds(field, prefixChildId, childFields)}
                        defaultChildValue={value[field.id]}
                        disabled={disabled}
                        variant={variant}
                      />
                    </DNABox>
                  ))}

                </DNABox>
              </Iffy>
              <Iffy is={groupCustomFields.groupType === 'SPAN'}>
                <DNABox fill appearance="col" spacing="md" keepSpacingLastItem>
                  {groupCustomFields.fields.map((field, i) => (
                    <ComposableField
                      key={`${value.id}-right-${i}`}
                      field={formatChildFieldIds(field, prefixChildId, childFields)}
                      defaultChildValue={value[field.id]}
                      disabled={disabled}
                      variant={variant}
                    />
                  ))}
                </DNABox>
              </Iffy>
            </DNABox>
          ))}
        </Iffy>
      </DNABox>
    </DNABox>
  )
}

interface HiddenFieldsProps {
  value: { [key: string]: string | string[] },
  control?: RHForm['control'],
  prefix: string
}

// TODO: find a better way to keep track of internal fields
const HiddenFields: React.FC<HiddenFieldsProps> = (props) => {
  const { value, control, prefix } = props
  return (
    <>
      {internalObjectFields.map((field) => {
        return (
          <HiddenField
            field={field}
            key={field.id}
            control={control}
            value={value[field.id]}
            prefix={prefix}
          />
        )
      })}
    </>
  )
}

interface HiddenField {
  field: CustomFieldDefinition,
  prefix?: string,
  control?: RHForm['control'],
  value?: any,
}

export const HiddenField = (props: HiddenField) => {
  const defaultValue = props.field.fieldType === FieldDataType.MULTICATEGORICAL ? [] : '';
  const { control, field, prefix, value = defaultValue } = props;
  const name = prefix ? `${prefix}.${field.id}` : field.id;

  return (
    <Controller
      key={field.id}
      name={name}
      control={control}
      defaultValue={value}
      render={(renderProps) => (
        <input
          type="hidden"
          {...renderProps}
        />
      )}
    />
  );
}

export const getValueFromChildField = (arr :object, fieldId: string) : string | string[] | undefined => {
  if (!fieldId.includes('.') || fieldId.split('.').length !== 3) return undefined
  const [parentFieldId, index, childFieldId] = fieldId.split('.')
  return arr[parentFieldId]?.[index]?.[childFieldId]
}

export const getErrorFromChildField = (arr :object, fieldId: string) : FieldError | undefined => {
  if (!fieldId.includes('.') || fieldId.split('.').length !== 3) return undefined
  const [parentFieldId, index, childFieldId] = fieldId.split('.')
  return arr[parentFieldId]?.[index]?.[childFieldId]
}
